

import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useAuth } from "../../hooks/AuthProvider";
import Spinner from "../../Components/Spinner";
import DataGridTable from "../../Components/DataGridTable";
import { LanguageContext } from "../../context/LanguageContext";
import moment from "moment";
import agentApi from "../../api/agentApi"; 

import { DecisionLevelBadge } from "../../Components/modal/DecisionLevelBadge";
import { AgentDecisionDetailsModal } from "../../Components/modal/AgentDecisionDetailsModal";
import agentTranslations from "../../languages/agent.json";

export const AgentDecisions = ({ 
  titleKey = "agent_decisions",
  tableKey = "agent_decisions", 
  baseFilters = {} 
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([{ field: "timestamp_utc", sort: "desc" }]);
  const [selectedDecision, setSelectedDecision] = useState(null);

  const { lang } = useContext(LanguageContext);
  const isRtl = ["ar", "ur"].includes(lang);
  const t = agentTranslations[lang] || agentTranslations.en;
  
  const pagingoptions = [10, 20, 50, 100];

  const fetchDecisions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize,
        filterModel,
        sortField: sortModel[0]?.field || "timestamp_utc",
        sortOrder: sortModel[0]?.sort || "desc",
      };

      const data = await agentApi.getDecisions(params, baseFilters);

      if (data && data.status === "ok") {
        setRows(data.items || []);
        setRowCount(data.count || 0);
      }
    } catch (error) {
      console.error("Error fetching agent decisions:", error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, JSON.stringify(filterModel), JSON.stringify(sortModel), JSON.stringify(baseFilters)]);
  useEffect(() => {
    fetchDecisions();
  }, [fetchDecisions]);

  const columns = useMemo(
    () => [
      { field: "room", headerName: t.table.room, width: 280 },
      { field: "decision_type", headerName: t.table.decisionType, width: 180 },
      {
        field: "level",
        headerName: t.table.level,
        width: 150,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => <DecisionLevelBadge level={params.row.level} />
      },
      {
        field: "execution_status",
        headerName: t.table.status,
        width: 160,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const isExecuted = params.row.execution_status === "executed";
          return (
            <div className="details-head" style={{ alignItems: "center", fontSize: "13px", textTransform: "capitalize" }}>
              <div className={`details-status ${isExecuted ? "active" : ""}`} style={{ backgroundColor: isExecuted ? "var(--accent)" : "orange" }}></div>
              {params.row.execution_status ? params.row.execution_status.replace(/_/g, " ") : ""}
            </div>
          );
        },
      },
      { field: "reason", headerName: t.table.reason, flex: 1, minWidth: 300 },
      {
        field: "timestamp_utc",
        headerName: t.table.time,
        width: 180,
        align: "center",
        headerAlign: "center",
        type: "dateTime",
        valueGetter: (params) => (params ? new Date(params) : null),
        valueFormatter: (params) => moment(params).format("DD/MM/YYYY HH:mm:ss"),
      },
      { field: "device_id", headerName: t.table.deviceId, width: 150, align: "center", headerAlign: "center" }
    ],
    [t]
  );

  return (
    <div className="configuration devices-page" style={{ display: "flex", flexDirection: "column", height: "100%" }} dir={isRtl ? "rtl" : "ltr"}>
      <div className="device-main">
        <div className="devices-header">
          <div className="devices-header-left" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2 className="devices-title">{t.titles[titleKey]}</h2>
          </div>
        </div>

        {loading && rows.length === 0 ? (
          <Spinner />
        ) : (
          <div className="devices-grid-wrapper">
            <DataGridTable
              columns={columns}
              rows={rows}
              idcolumn={"decision_id"}
              pagingoptions={pagingoptions}
              sortField={"timestamp_utc"}
              sortType="desc"
              showToolbar={true}
              tableKey={tableKey}
              loading={loading}
              handleRowClick={(params) => setSelectedDecision(params.row)}
              pagination={{
                serverSide: true,
                page,
                pageSize,
                count: rowCount,
                onPageChange: (newPage) => setPage(newPage),
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                },
                onSortModelChange: (newModel) => {
                  setSortModel(newModel && newModel.length ? newModel : []);
                  setPage(1);
                },
                onFilterModelChange: (newModel) => {
                  setFilterModel(newModel || { items: [] });
                  setPage(1);
                },
              }}
            />
          </div>
        )}
      </div>

      <AgentDecisionDetailsModal 
        open={Boolean(selectedDecision)} 
        data={selectedDecision} 
        onClose={() => setSelectedDecision(null)}
        t={t.modal}
      />
    </div>
  );
};